/**
 * -----------------------------------------------------------------------------
 * Groups Listing Page
 * -----------------------------------------------------------------------------
 * This page is used to list all the groups of the organization..
 */

import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useDebouncedCallback } from "use-debounce";
import { useOrganizationGroupsQuery } from "../../../api/network/adminApiServices";
import {
  TListingQueryParams,
  TModalsState,
  setListingQueryParams,
  setModalsData,
} from "../../../api/store/commonSlice";
import { TLegacyGroup } from "../../../api/types/Group";
import { TSelectboxOption } from "../../../components/admin/formFields";
import HeaderView from "../../../components/admin/headerView";
import AdminTable from "../../../components/adminTable";
import ListingPageSubHeader from "../../../components/listingPageSubHeader";
import ListingTableHeader from "../../../components/listingTableHeader";
import {
  EditListingColumnsButton,
  ModalEditListingColumns,
  TListingColumn,
} from "../../../components/modalEditListingColumns";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import { TAdminTableRowData } from "../../../components/types";
import { APP_CONFIG } from "../../../constants/constants";
import { routeUrls } from "../../../navigation/routeUrls";
import { SortingStatusWithReset } from "../../../utils/adminListings";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";
import AdminsGroupsCreateNew from "./createNewGroup";

const all_columns = [
  "Name",
  "Description",
  "No. Of Active Assets",
  "No. Of Inactive Assets",
  "No. Of Active Users",
  "No. Of Inactive Users",
  // "Parent Group",
];

const columnOrderingFields: Record<string, string> = {
  Name: "groupid",
  Description: "description",
  "No. Of Active Assets": "active_vehicle_count",
  "No. Of Inactive Assets": "inactive_vehicle_count",
  "No. Of Active Users": "active_user_count",
  "No. Of Inactive Users": "inactive_user_count",
};

const ScreenDashboardAdminGroupsListing = () => {
  const { t: tMain } = useTranslation();
  const { t } = useTranslation("translation", { keyPrefix: "admins.groups" });
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { groups: orgGroupsQueryParams } = listingQueryParams;
  const dispatch = useDispatch();
  const listingWrapperRef = React.useRef<HTMLDivElement>(null);

  // columns
  const { getLocalStorageItem } = useLocalStorage();
  const savedColumns = getLocalStorageItem(localStorageKeys.columns.Groups);
  const [columns, setColumns] = React.useState<TListingColumn[]>(
    all_columns.map((colItem, _) => {
      return {
        name: colItem,
        show: !!savedColumns ? savedColumns.includes(colItem) : true,
      };
    })
  );

  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        groups: { ...orgGroupsQueryParams, page: 1, search: value },
      })
    );
  }, 500);

  const handleOrderingChange = (ordering?: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        groups: {
          ...orgGroupsQueryParams,
          ordering,
          page: 1,
        },
      })
    );
  };

  // fetch groups data
  const {
    data: dataOrgGroups,
    isFetching: isFetchingOrgGroups,
    error,
  } = useOrganizationGroupsQuery(
    {
      ...orgGroupsQueryParams,
      // 'search_fields' intentionally not added here as default search fields are defined in the API service
    },
    {
      // ensure groups list refreshes after a change made to user's group assignments
      refetchOnMountOrArgChange: true,
    }
  );
  const { count, next, previous, results } = dataOrgGroups || {};

  // smooth scroll listing to the top on page change
  useEffect(() => {
    listingWrapperRef?.current?.scrollTo({ behavior: "smooth", top: 0 });
  }, [dataOrgGroups]);

  // preparing table data
  const tableData: TAdminTableRowData[] = !!results
    ? (results || ([] as TLegacyGroup[])).map(
        (item: TLegacyGroup, index: number) => ({
          navLink: `${routeUrls.dashboardChildren.adminChildren.groups}/${item.groupid}`,
          cellData: [
            item?.displayname || item?.groupid, // "Group Name",
            item?.description || "-", // "Description",
            item?.active_inactive_vehicle_counts?.active_vehicle_count, // "Active Vehicles", // TODO: Need to update this
            item?.active_inactive_vehicle_counts?.inactive_vehicle_count, // "Inactive Vehicles", // TODO: Need to update this
            item?.active_inactive_user_counts?.active_user_count, // "Active Users"
            item?.active_inactive_user_counts?.inactive_user_count, // "Inactive Users"
            // !!item?.parentgroupid && item?.parentgroupid !== 'undefined' // "Parent Group"
            //   ? item?.parentgroupid
            //   : '-',
          ].filter((_, index) => columns[index].show),
        })
      )
    : [];

  // rendering
  return (
    <>
      <HeaderView title={t("heading")} />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} pt-4`}>
        <ListingPageSubHeader
          heading={t("sub_heading")}
          buttonText={t("add_new")}
          buttonCallback={() => {
            dispatch(
              setModalsData({
                ...modalsState,
                createGroupUnder: "",
                showCreateGroup: true,
              })
            );
          }}
        />
        <ListingTableHeader
          heading={
            <div className="flex items-center gap-4">
              {/* Heading */}
              <span>{t("listing_heading")}</span>

              {/* Sorted by label */}
              {!!orgGroupsQueryParams?.ordering && (
                <SortingStatusWithReset
                  ordering={orgGroupsQueryParams.ordering}
                  columnOrderingFields={columnOrderingFields}
                  onResetOrdering={() => handleOrderingChange(undefined)}
                />
              )}
            </div>
          }
          searchBoxPlaceholder={t("search_placeholder")}
          searchBoxValue={orgGroupsQueryParams.search}
          searchBoxOnChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            debouncedSetSearchKeyword(e.target.value)
          }
          searchBoxOnClear={() => debouncedSetSearchKeyword("")}
        />
        <ModalEditListingColumns
          columns={columns}
          setColumns={setColumns}
          lsKey={localStorageKeys.columns.Groups}
        />
        <div className="py-4 mt-4 relative">
          <EditListingColumnsButton disabled={isFetchingOrgGroups} />
          <AdminTable
            viewportHeightOffset={"400"}
            columns={columns
              .filter((item) => item.show)
              .map((item) => item.name)}
            data={tableData}
            isLoading={isFetchingOrgGroups}
            listingQueryParams={orgGroupsQueryParams}
            listingWrapperRef={listingWrapperRef}
            onOrderingChange={handleOrderingChange}
            ordering={orgGroupsQueryParams.ordering}
            sortableColumns={columnOrderingFields}
          />
          {!isFetchingOrgGroups && (
            <Pagination
              pageSize={orgGroupsQueryParams.page_size}
              handlePageSizeChange={(e: TSelectboxOption | null) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    groups: {
                      ...orgGroupsQueryParams,
                      page: 1,
                      page_size: parseInt(`${e?.value}`),
                    },
                  })
                );
              }}
              totalPages={
                count ? Math.ceil(count / orgGroupsQueryParams.page_size) : 1
              }
              forcePage={orgGroupsQueryParams.page - 1}
              handlePageClick={(data: TPaginationSelected) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    groups: {
                      ...orgGroupsQueryParams,
                      page: data?.selected + 1,
                    },
                  })
                );
              }}
              paginCurrentPage={orgGroupsQueryParams.page}
              paginItemsOnCurrentPage={results?.length || 0}
              paginTotalItemsCount={count}
            />
          )}
        </div>
      </div>
      <AdminsGroupsCreateNew />
    </>
  );
};

export default ScreenDashboardAdminGroupsListing;
